import { css } from "@emotion/react";
import styled from "@emotion/styled";
import Markdown from "markdown-to-jsx";
import React from "react";

import { colorPalette } from "@every.org/common/src/display/palette";
import { TextSize } from "@every.org/common/src/display/text";

import { Link, LinkAppearance } from "src/components/Link";
import { spacing } from "src/theme/spacing";
import { textSizeCss } from "src/theme/text";
import { AnnouncementRule } from "src/utility/announcements";

const BannerContainer = styled.div<{ announcementStyle?: "info" | "warn" }>`
  width: 100%;
  padding: ${spacing.s} ${spacing.m};
  text-align: center;

  ${({ announcementStyle }) =>
    announcementStyle === "info" &&
    css`
      background-color: ${colorPalette.teal};
    `}

  ${({ announcementStyle }) =>
    announcementStyle === "warn" &&
    css`
      background-color: ${colorPalette.red};
    `}
`;

const BannerText = styled.p`
  margin: 0;
  color: ${colorPalette.white};
  font-weight: 600;
  ${textSizeCss[TextSize.s]}

  a,
  a:hover {
    color: inherit;
    text-decoration: underline;
  }
`;

// markdown-to-jsx passes a null href for unsafe URLs such as javascript:
function BannerLink({ href, children }: React.ComponentProps<"a">) {
  return href ? (
    <Link
      to={href}
      appearance={LinkAppearance.UNSTYLED}
      target="_blank"
      rel="noopener"
      data-tname="announcement-banner-link"
    >
      {children}
    </Link>
  ) : (
    <span>{children}</span>
  );
}

export const AnnouncementBanner: React.FCC<{
  announcement: AnnouncementRule;
  className?: string;
}> = ({ announcement, className }) => {
  const style = announcement.style || "warn";

  return (
    <BannerContainer className={className} announcementStyle={style}>
      <BannerText>
        <Markdown
          options={{
            forceInline: true,
            disableParsingRawHTML: true,
            overrides: { a: BannerLink },
          }}
        >
          {announcement.msg}
        </Markdown>
      </BannerText>
    </BannerContainer>
  );
};
